import React, { useState } from 'react';
import {
  Box,
  Header,
  SpaceBetween,
  Container,
  Button,
  FormField,
  Input,
  ColumnLayout,
  StatusIndicator,
  Alert,
  Modal,
  CopyToClipboard,
  Link,
  Checkbox,
} from '@cloudscape-design/components';

import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Distributor } from '../../common/types';
import {
  getDistributor,
  updateDistributor,
  disableDistributor,
  enableDistributor,
} from '../../common/api/distributors';
import { LoadingOrError, ResourceError } from '../../components/Loading';
import { formatDateString, formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { ReportTable } from '../../components/dashboard/ReportTable';
import { PhoneNumber } from '../../components/PhoneNumber';
import { Email } from '../../components/Email';
import { DistributorHierarchy } from '../../components/distributors/DistributorHierarchy';

// We're already in the scorecard app
const scorecardUrl = window.location.origin;

export default function DistributorPage() {
  const { uuid } = useParams({ from: '/dashboard/distributors_/$uuid' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [showPathWarning, setShowPathWarning] = useState(false);
  const [editedDistributor, setEditedDistributor] = useState<Partial<Distributor>>({});
  const [pendingPathChange, setPendingPathChange] = useState<string | null>(null);
  const [now, setNow] = useState(new Date());

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['distributors', uuid],
    queryFn: () => getDistributor(uuid!),
    enabled: !!uuid,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<Distributor>) => updateDistributor(uuid!, updates),
    onSuccess: () => {
      setIsEditing(false);
      setEditedDistributor({});
      queryClient.invalidateQueries({ queryKey: ['distributors', uuid] });
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => disableDistributor(uuid!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distributors', uuid] });
    },
  });

  const enableMutation = useMutation({
    mutationFn: () => enableDistributor(uuid!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distributors', uuid] });
    },
  });

  const handleSave = () => {
    // Check if path is being changed
    if (editedDistributor.path && editedDistributor.path !== data?.path) {
      setPendingPathChange(editedDistributor.path);
      setShowPathWarning(true);
    } else {
      updateMutation.mutate(editedDistributor);
    }
  };

  const confirmPathChange = () => {
    setShowPathWarning(false);
    updateMutation.mutate(editedDistributor);
    setPendingPathChange(null);
  };

  if (isPending) {
    return (
      <DashboardViewLayout
        breadcrumbs={[
          { text: 'Distributors', href: '/dashboard/distributors' },
          { text: 'Loading...', href: '' },
        ]}
        contentType="default"
      >
        <Container>
          <LoadingOrError loading retry={refetch} />
        </Container>
      </DashboardViewLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardViewLayout
        breadcrumbs={[
          { text: 'Distributors', href: '/dashboard/distributors' },
          { text: 'Error', href: '' },
        ]}
        contentType="default"
      >
        <ResourceError
          error={error ?? new Error('Distributor not found')}
          refetch={refetch}
          resourceName="Distributor"
        />
      </DashboardViewLayout>
    );
  }

  const currentValue = (field: keyof Distributor) => {
    if (isEditing && field in editedDistributor) {
      return editedDistributor[field] as string;
    }
    return data[field] as string;
  };

  const currentBoolean = (field: keyof Distributor) => {
    if (field in editedDistributor) {
      return editedDistributor[field] as boolean;
    }
    return data[field] as boolean;
  };

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Distributors', href: '/dashboard/distributors' },
        { text: `${data.first_name} ${data.last_name}`, href: '' },
      ]}
      contentType="default"
    >
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {isEditing ? (
                    <>
                      <Button
                        variant="link"
                        onClick={() => {
                          setIsEditing(false);
                          setEditedDistributor({});
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleSave}
                        loading={updateMutation.isPending}
                      >
                        Save Changes
                      </Button>
                    </>
                  ) : (
                    <>
                      {data.disabled ? (
                        <Button
                          variant="normal"
                          iconName="undo"
                          onClick={() => enableMutation.mutate()}
                          loading={enableMutation.isPending}
                        >
                          Enable
                        </Button>
                      ) : (
                        <Button
                          variant="normal"
                          iconName="remove"
                          onClick={() => disableMutation.mutate()}
                          loading={disableMutation.isPending}
                        >
                          Disable
                        </Button>
                      )}
                      <Button variant="primary" iconName="edit" onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    </>
                  )}
                </SpaceBetween>
              }
            >
              {data.first_name} {data.last_name}
            </Header>
          }
        >
          <SpaceBetween size="l">
            {data.disabled && (
              <Alert type="warning">
                This distributor is currently disabled and will not appear in the distributors list.
              </Alert>
            )}

            <ColumnLayout columns={2} variant="text-grid">
              <SpaceBetween size="l">
                <FormField
                  label="Scorecard Page"
                  constraintText={
                    isEditing ? 'Lowercase letters, numbers, and hyphens only' : undefined
                  }
                >
                  {isEditing ? (
                    <SpaceBetween size="xxs" direction="horizontal" alignItems="center">
                      <Box variant="code">/d/</Box>
                      <Input
                        value={currentValue('path')}
                        onChange={({ detail }) =>
                          setEditedDistributor({
                            ...editedDistributor,
                            path: detail.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                          })
                        }
                      />
                    </SpaceBetween>
                  ) : (
                    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                      <Link
                        href={`${scorecardUrl}/d/${data.path}`}
                        target="_blank"
                        variant="primary"
                      >
                        {scorecardUrl}/d/{data.path}
                      </Link>
                      <CopyToClipboard
                        copyButtonAriaLabel="Copy URL"
                        copyErrorText="URL failed to copy"
                        copySuccessText="URL copied"
                        textToCopy={`${scorecardUrl}/d/${data.path}`}
                        variant="icon"
                      />
                    </SpaceBetween>
                  )}
                </FormField>

                {data.parent_uuid && (
                  <FormField label="Parent Distributor">
                    <SpaceBetween size="xxs">
                      <Link
                        href={`/dashboard/distributors/${data.parent_uuid}`}
                        onFollow={(event) => {
                          event.preventDefault();
                          navigate({ to: `/dashboard/distributors/${data.parent_uuid}` });
                        }}
                      >
                        {[data.parent_first_name, data.parent_last_name]
                          .filter(Boolean)
                          .join(' ') || data.parent_path}
                      </Link>
                      {data.parent_company && (
                        <Box color="text-body-secondary">{data.parent_company}</Box>
                      )}
                      {data.parent_path && (
                        <Box color="text-body-secondary">/d/{data.parent_path}</Box>
                      )}
                    </SpaceBetween>
                  </FormField>
                )}

                <FormField label="First Name">
                  {isEditing ? (
                    <Input
                      value={currentValue('first_name')}
                      onChange={({ detail }) =>
                        setEditedDistributor({ ...editedDistributor, first_name: detail.value })
                      }
                    />
                  ) : (
                    <Box variant="p">{data.first_name}</Box>
                  )}
                </FormField>

                <FormField label="Last Name">
                  {isEditing ? (
                    <Input
                      value={currentValue('last_name')}
                      onChange={({ detail }) =>
                        setEditedDistributor({ ...editedDistributor, last_name: detail.value })
                      }
                    />
                  ) : (
                    <Box variant="p">{data.last_name}</Box>
                  )}
                </FormField>
              </SpaceBetween>

              <SpaceBetween size="l">
                <FormField label="Email">
                  {isEditing ? (
                    <Input
                      type="email"
                      value={currentValue('email')}
                      onChange={({ detail }) =>
                        setEditedDistributor({ ...editedDistributor, email: detail.value })
                      }
                    />
                  ) : (
                    <Email email={data.email} />
                  )}
                </FormField>

                <FormField label="Phone Number">
                  {isEditing ? (
                    <Input
                      value={currentValue('phone_number') ?? ''}
                      onChange={({ detail }) =>
                        setEditedDistributor({ ...editedDistributor, phone_number: detail.value })
                      }
                      placeholder="+1 555 123 4567"
                    />
                  ) : (
                    <PhoneNumber
                      phoneNumber={data.phone_number}
                      lineTypeInfo={data.phone_line_type_info}
                      showLineTypeInfo
                    />
                  )}
                </FormField>

                <FormField label="Status">
                  {data.disabled ? (
                    <StatusIndicator type="stopped">Disabled</StatusIndicator>
                  ) : (
                    <StatusIndicator type="success">Active</StatusIndicator>
                  )}
                </FormField>

                <FormField label="Created">
                  <Box variant="p">{formatDateStringWithTime(data.created_at)}</Box>
                </FormField>

                <FormField label="Last Updated">
                  <Box variant="p">{formatDateStringWithTime(data.updated_at)}</Box>
                </FormField>
              </SpaceBetween>
            </ColumnLayout>

            {isEditing && (
              <FormField label="Show on WordPress">
                <SpaceBetween size="xs">
                  {(
                    [
                      ['show_first_name', 'First name'],
                      ['show_last_name', 'Last name'],
                      ['show_company', 'Company'],
                      ['show_email', 'Email'],
                      ['show_phone', 'Phone'],
                      ['show_additional_info', 'Additional info'],
                    ] as const
                  ).map(([field, label]) => (
                    <Checkbox
                      key={field}
                      checked={currentBoolean(field)}
                      onChange={({ detail }) =>
                        setEditedDistributor({ ...editedDistributor, [field]: detail.checked })
                      }
                    >
                      {label}
                    </Checkbox>
                  ))}
                </SpaceBetween>
              </FormField>
            )}
          </SpaceBetween>
        </Container>

        <DistributorHierarchy key={data.uuid} rootUuid={data.uuid} />

        {/* Reports section */}
        <Container>
          <ReportTable
            variant="embedded"
            now={now}
            setNow={setNow}
            query={{
              tokens: [
                {
                  propertyKey: 'distributor_path',
                  value: data.path,
                  operator: '=',
                },
              ],
              operation: 'and',
            }}
            hideFilter
            hideColumns={['user']}
          />
        </Container>
      </SpaceBetween>

      {/* Path Change Warning Modal */}
      <Modal
        visible={showPathWarning}
        onDismiss={() => {
          setShowPathWarning(false);
          setPendingPathChange(null);
        }}
        header="Warning: Changing Custom Path"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  setShowPathWarning(false);
                  setPendingPathChange(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmPathChange}
                loading={updateMutation.isPending}
              >
                Confirm Change
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Alert type="warning">
          <SpaceBetween size="xs">
            <Box>
              Changing the custom path from <strong>/{data.path}</strong> to{' '}
              <strong>/{pendingPathChange}</strong> will break any existing links to this
              distributor&apos;s scorecard page.
            </Box>
            <Box>Any bookmarks or shared links using the old path will no longer work.</Box>
          </SpaceBetween>
        </Alert>
      </Modal>
    </DashboardViewLayout>
  );
}
